'use client'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { snapshotAndUpdateQueryOptions } from '@/service/snapshot-query-options'
import { fetchTreeNewsHistory } from '@/service/tree-news.api'
import type { TreeNewsHistoryRequest, TreeNewsPostList } from '@/types/generated/public_api'

export { useTreeNewsStream } from '@/service/ws/use-tree-news-stream'

export const treeNewsKeys = {
  all: ['tree-news'] as const,
  history: (params: TreeNewsHistoryRequest) => [...treeNewsKeys.all, 'history', params] as const,
}

export function useTreeNewsHistory(params: TreeNewsHistoryRequest = {}, enabled = true) {
  return useQuery({
    queryKey: treeNewsKeys.history(params),
    queryFn: () => fetchTreeNewsHistory(params),
    enabled,
    ...snapshotAndUpdateQueryOptions,
  })
}

export function useTreeNewsInfinite(params: TreeNewsHistoryRequest = {}, enabled = true) {
  return useInfiniteQuery<TreeNewsPostList>({
    queryKey: treeNewsKeys.history(params),
    queryFn: ({ pageParam = 0 }) =>
      fetchTreeNewsHistory({
        ...params,
        offset: pageParam as number,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.posts.length < (params.limit ?? 50)) return undefined
      return allPages.length * (params.limit ?? 50)
    },
    initialPageParam: 0,
    enabled,
    // History under a live stream: fetched on mount, then the stream keeps it current. The
    // defaults (5s stale, refetch on focus) re-requested every loaded page, one by one.
    ...snapshotAndUpdateQueryOptions,
    ...snapshotAndUpdateQueryOptions,
  })
}
